import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DBButton from "../../component/global/DBButton";
import SurveyFormRenderer from "./SurveyFormRenderer";

function sortCriteria(criteria) {
    return [...criteria].sort(
        (a, b) => (a.weight ?? 0) - (b.weight ?? 0)
    );
}

export default function MultiStepSurveyFormRenderer({
    criteria,
    replies,
    onChange,
    onFinish,
    readOnly = false,
    finishLoading = false,
    finishDisabled = false,
    finishCaption,
}) {
    const { t } = useTranslation();
    const [stepIndex, setStepIndex] = useState(0);

    const orderedCriteria = useMemo(
        () => sortCriteria(criteria || []),
        [criteria]
    );
    const totalSteps = orderedCriteria.length;
    const currentCriterion = orderedCriteria[stepIndex];
    const isLastStep = stepIndex === totalSteps - 1;

    const goToStep = (nextStep) => {
        setStepIndex(nextStep);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleNext = () => {
        if (!isLastStep) {
            goToStep(stepIndex + 1);
        }
    };

    const handlePrevious = () => {
        if (stepIndex > 0) {
            goToStep(stepIndex - 1);
        }
    };

    if (!totalSteps) {
        return null;
    }

    return (
        <div className="survey-step-form">
            <div className="survey-step-progress">
                <span className="survey-step-progress-count">
                    {t("survey.form.step.progress", {
                        current: stepIndex + 1,
                        total: totalSteps,
                    })}
                </span>
                <div className="survey-step-progress-bar">
                    <div
                        className="survey-step-progress-fill"
                        style={{
                            width: `${((stepIndex + 1) / totalSteps) * 100}%`,
                        }}
                    />
                </div>
            </div>

            <SurveyFormRenderer
                criteria={orderedCriteria}
                replies={replies}
                onChange={onChange}
                readOnly={readOnly}
                onlyCriterionId={currentCriterion?.id}
            />

            <div className="survey-step-nav">
                {stepIndex > 0 && (
                    <DBButton
                        type="primary"
                        outlined
                        className="survey-step-nav-btn"
                        caption={t("prev.label")}
                        onClick={handlePrevious}
                    />
                )}
                {!isLastStep && (
                    <DBButton
                        className="survey-step-nav-btn"
                        caption={t("next.label")}
                        onClick={handleNext}
                    />
                )}
                {!readOnly && (
                    <DBButton
                        type="primary"
                        outlined={!isLastStep}
                        className="survey-step-nav-btn"
                        caption={finishCaption || t("finish.label")}
                        loading={finishLoading}
                        disabled={finishDisabled}
                        onClick={onFinish}
                    />
                )}
            </div>
        </div>
    );
}
